import type { FC, ReactNode } from 'react';
import { lazy } from 'react';
import { useParams } from 'react-router-dom';
import urltemplate from 'url-template';
import type { ContractManagementResponse } from 'src/apis/contract-management/types';
import { useContractManagement } from 'src/apis/contract-management/use-contract-management';
import { useContractRelevantTransferOfUses } from 'src/apis/contract-management/use-contract-relevant-transfer-of-uses';
import { useContractTerminationConsequences } from 'src/apis/contract-management/use-contract-termination-consequences';
import { useDect } from 'src/apis/contract-management/use-dect';
import { PageSpinner } from 'src/components/spinner-container';
import { useLect } from 'src/pages/transport/secondary-trading/trade/use-lect';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

type TerminationContractType = 'lect' | 'dect';

type RouteParams = {
  contractId: string;
  contractType: TerminationContractType;
};

export const TerminateContract: FC = () => {
  const { contractId, contractType } = useParams<RouteParams>();
  const contractManagement = useContractManagement();

  if (!contractManagement.response) return <PageSpinner />;

  return (
    <LoadContract
      contractId={contractId}
      contractType={contractType}
      contractManagement={contractManagement.response.data}
    >
      {({ assignedId, terminationConsequencesUrl }) => (
        <LoadTerminationConsequences
          contractType={contractType}
          assignedId={assignedId}
          contractId={contractId}
          terminationConsequencesUrl={terminationConsequencesUrl}
        />
      )}
    </LoadContract>
  );
};

type ContractData = {
  assignedId: string;
  terminationConsequencesUrl: string;
};

type LoadContractProps = {
  contractId: string;
  contractType: TerminationContractType;
  contractManagement: ContractManagementResponse;
  children: (data: ContractData) => ReactNode;
};

const LoadContract: FC<LoadContractProps> = ({
  contractId,
  contractType,
  contractManagement,
  children,
}) => {
  switch (contractType) {
    case 'lect':
      if (!contractManagement._links.getLect) throw new Forbidden();
      return (
        <LoadLect
          url={urltemplate
            .parse(contractManagement._links.getLect.href)
            .expand({ lectId: contractId })}
        >
          {children}
        </LoadLect>
      );
    case 'dect':
      if (!contractManagement._links.getDect) throw new Forbidden();
      return (
        <LoadDect
          url={urltemplate
            .parse(contractManagement._links.getDect.href)
            .expand({ dectId: contractId })}
        >
          {children}
        </LoadDect>
      );
  }
};

const LoadDect: FC<{
  url: string;
  children: (data: ContractData) => ReactNode;
}> = ({ url, children }) => {
  const dect = useDect({ url });

  if (!dect.response) return <PageSpinner />;

  const terminationConsequencesUrl =
    dect.response.data._links.terminationConsequences?.href;

  if (!terminationConsequencesUrl) throw new Forbidden();

  return children({
    assignedId: dect.response.data.assignedId,
    terminationConsequencesUrl,
  });
};

const LoadLect: FC<{
  url: string;
  children: (data: ContractData) => ReactNode;
}> = ({ url, children }) => {
  const lect = useLect({ url });

  if (!lect.response) return <PageSpinner />;

  const terminationConsequencesUrl =
    lect.response.data._links.terminationConsequences?.href;

  if (!terminationConsequencesUrl) throw new Forbidden();

  return children({
    assignedId: lect.response.data.assignedId,
    terminationConsequencesUrl,
  });
};

type LoadTerminationConsequencesProps = {
  contractType: TerminationContractType;
  assignedId: string;
  contractId: string;
  terminationConsequencesUrl: string;
};

const LoadTerminationConsequences: FC<LoadTerminationConsequencesProps> = ({
  contractType,
  assignedId,
  contractId,
  terminationConsequencesUrl,
}) => {
  const contractTerminationConsequences = useContractTerminationConsequences({
    url: terminationConsequencesUrl,
  });
  const relevantTransferOfUses = useContractRelevantTransferOfUses({
    contractTerminationConsequences,
  });

  if (
    !contractTerminationConsequences.response ||
    !relevantTransferOfUses.response
  ) {
    return <PageSpinner />;
  }

  return (
    <Page
      contractType={contractType}
      assignedId={assignedId}
      contractId={contractId}
      contractTerminationConsequences={contractTerminationConsequences}
      relevantTransferOfUses={relevantTransferOfUses}
    />
  );
};
